// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers. The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import buttonSizeVariants from "@awc/MobiusOverrides/Button/buttonSizeVariants";
import { fontConfig } from "@awc/XDL/PreStyle/typography";
import { xBaseTheme } from "@awc/XDL/types";
import { color, getSystemColor } from "@awc/XDL/Utilities";
import { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import ButtonPresentationPropsDefault from "@insite/mobius/Button/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import get from "@insite/mobius/utilities/get";
import { css } from "styled-components";

export const buttonTransparent = css`
    &,
    &:focus:not([disabled]),
    &:active:not([disabled]),
    &:disabled,
    &:focus:hover,
    &:hover:not(:disabled) {
        border: 0;
        background-color: transparent;
        box-shadow: none;
        outline: 0;
        transform: translate(0, 0);
        min-height: initial;
    }
`;

// Button color configurations
export const buttonColors: { [key: string]: { [key: string]: string } } = {
    primary: {
        main: "primary.main",
        hover: "common.ui8",
        focus: "common.ui9",
        disabled: "common.ui16",
    },
    secondary: {
        main: "primary.main",
        hover: "common.ui8",
        focus: "common.ui9",
        disabled: "common.ui16",
    },
    tertiary: {
        main: "secondary.main",
        hover: "common.ui10",
        focus: "common.ui11",
        disabled: "common.ui16",
    },
    link: {
        main: "primary.main",
        hover: "common.ui8",
        focus: "common.ui9",
        disabled: "common.ui16",
    },
    addToCart: {
        main: "secondary.main",
        hover: "common.ui10",
        focus: "common.ui11",
        disabled: "common.ui16",
    },
    muted: {
        main: "common.ui20",
        hover: "primary.main",
        focus: "common.ui11",
        disabled: "common.ui16",
    },
};
const getSizeRelatedStyles = (size: keyof typeof buttonSizeVariants) => {
    return css`
        min-height: ${get(buttonSizeVariants, [size, "height"])}px;
        line-height: ${get(buttonSizeVariants, [size, "lineHeight"])};
        padding: ${get(buttonSizeVariants, [size, "padding"])};
    `;
};

export const sharedButtonStyles = css`
    display: flex;
    align-items: center;
    justify-content: center;
    ${({ _sizeVariant }: any) => getSizeRelatedStyles(_sizeVariant)}

    ${ButtonIcon} {
        top: 0;
    }

    &.small {
        ${getSizeRelatedStyles("small")}
    }
    &.medium {
        ${getSizeRelatedStyles("medium")}
    }
    &.large {
        ${getSizeRelatedStyles("large")}
    }
    &:focus-visible {
        outline: 1px solid ${getSystemColor("primary")};
    }
`;

const primaryButtonStyle: ButtonPresentationProps = {
    ...ButtonPresentationPropsDefault,
    color: buttonColors.primary.main,
    buttonType: "solid",
    mergeCss: true,
    hoverStyle: {
        background: `${getSystemColor(buttonColors.primary.hover)}`,
        borderColor: `${getSystemColor(buttonColors.primary.hover)}`,
        color: `${getSystemColor("common.white")}`,
    },
    typographyProps: {
        color: "common.white",
    },
    css: css`
        color: ${color("common.white")};
        border-radius: 8px;
        border-width: 2px;
        &:focus {
            background: ${getSystemColor(buttonColors.primary.focus)};
            border-color: ${getSystemColor(buttonColors.primary.focus)};
        }
        &&:disabled {
            background: ${getSystemColor(buttonColors.primary.disabled)};
            border-color: transparent;
            &,
            ${TypographyStyle} {
                color: ${getSystemColor("common.ui4")};
            }
        }
        ${IconWrapper} {
            color: ${getSystemColor("common.white")};
        }
        ${sharedButtonStyles}
    `,
};

export default {
    primary: primaryButtonStyle,
    secondary: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.secondary.main,
        buttonType: "outline",
        mergeCss: true,
        hoverStyle: {
            borderColor: `${getSystemColor(buttonColors.secondary.hover)}`,
            color: `${getSystemColor(buttonColors.secondary.hover)}`,
        },

        css: css`
            border-radius: 8px;
            border-width: 2px;
            &&:focus:not(:disabled) {
                border-color: ${getSystemColor(buttonColors.secondary.focus)};
                color: ${getSystemColor(buttonColors.secondary.focus)};
            }
            &&:disabled {
                border-color: ${getSystemColor(buttonColors.secondary.disabled)};
                &,
                ${TypographyStyle} {
                    color: ${getSystemColor("common.ui4")};
                }
            }
            ${sharedButtonStyles}
        `,
    },
    tertiary: primaryButtonStyle,
    link: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.link.main,
        buttonType: "outline",
        mergeCss: true,
        hoverStyle: {
            borderColor: `${getSystemColor(buttonColors.link.hover)}`,
            color: `${getSystemColor(buttonColors.link.hover)}`,
        },
        css: css`
            ${buttonTransparent}
            padding: 0;
        `,
    },
    addToCart: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.addToCart.main,
        buttonType: "outline",
        mergeCss: true,
        hoverStyle: {
            background: "transparent",
            borderColor: `${getSystemColor(buttonColors.addToCart.hover)}`,
            color: `${getSystemColor(buttonColors.addToCart.hover)}`,
        },
        typographyProps: {
            weight: fontConfig.weight.bold,
            css: css`
                color: inherit;
            `,
        },
        css: css`
            border-radius: 8px;
            border-width: 2px;
            color: ${getSystemColor(buttonColors.addToCart.main)};

            &:focus {
                background: transparent;
                border-color: ${getSystemColor(buttonColors.addToCart.focus)};
                color: ${getSystemColor(buttonColors.addToCart.focus)};
            }
            &&:disabled {
                background: transparent;
                border-color: ${getSystemColor(buttonColors.addToCart.disabled)};
                &,
                ${TypographyStyle} {
                    color: ${getSystemColor("common.ui4")};
                }
            }

            ${sharedButtonStyles}
        `,
    },
    muted: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.muted.main,
        buttonType: "solid",
        mergeCss: true,
        hoverStyle: {
            background: `${getSystemColor(buttonColors.muted.main)}`,
            borderColor: `${getSystemColor(buttonColors.muted.main)}`,
            color: `${getSystemColor(buttonColors.muted.hover)}`,
        },
        typographyProps: {
            color: "common.ui21",
            weight: fontConfig.weight.bold,
        },
        css: css`
            border-radius: 8px;
            color: ${getSystemColor(buttonColors.muted.main)};

            &:focus {
                background: ${getSystemColor(buttonColors.muted.main)};
                border-color: ${getSystemColor(buttonColors.muted.main)};
                color: ${getSystemColor(buttonColors.muted.hover)};
            }
            &:hover {
                & > ${TypographyStyle} {
                    color: ${getSystemColor(buttonColors.muted.hover)};
                }
            }
            &&:disabled {
                background: transparent;
                border-color: ${getSystemColor(buttonColors.addToCart.disabled)};
                color: ${getSystemColor(buttonColors.muted.disabled)};
            }

            ${sharedButtonStyles}
        `,
    },
} as xBaseTheme["button"] & {
    link: ButtonPresentationProps;
    addToCart: ButtonPresentationProps;
};
